import { api } from './api.js'
import { RANGE_DAYS } from '../features/calendar/CalendarPage.jsx'
import { saveBuilderCopy } from '../features/assistant/offlineCopy.js'
import { todayISO, addDays } from '../features/assistant/lib.js'

/**
 * Offline copy of CDM Information: walking every Information read pushes
 * each response through the service worker's cache, so the repository stays
 * readable with no connection. Session Log is deliberately never synced.
 *
 * iOS gives web apps no background execution, so "keep it current" means
 * refreshing on open: maybeAutoSync() runs on every launch and quietly
 * re-walks if the copy is stale. The button on More does the same walk
 * loudly (with progress) and works as a force-refresh.
 */

const KEY = 'cdm-offline-synced-at'
export const SYNC_INTERVAL_MS = 6 * 60 * 60 * 1000 // stale after 6 h

export function lastSyncedAt() {
  return Number(localStorage.getItem(KEY) || 0)
}

/** True when a service worker controls the page — i.e. offline can work. */
export function offlineReady() {
  return 'serviceWorker' in navigator && Boolean(navigator.serviceWorker.controller)
}

export async function syncInformation(onProgress) {
  let n = 0
  await api('/auth/me')
  await api('/clients')
  await api('/credentials').catch(() => {}) // labels only; fine if forbidden
  await api('/credentials?collection=embargo').catch(() => {})
  const { spaces } = await api('/spaces')
  for (const s of spaces) {
    const list = await api(`/spaces/${s.slug}/pages`)
    for (const p of list.pages || []) {
      await api(`/spaces/${s.slug}/pages/${p.slug}`).catch(() => {})
      n++
      onProgress?.(n)
    }
  }
  localStorage.setItem(KEY, String(Date.now()))
  return n
}

/** Call once per app launch. No-ops in dev, offline, or when fresh. */
export function maybeAutoSync() {
  if (!offlineReady() || !navigator.onLine) return
  if (Date.now() - lastSyncedAt() < SYNC_INTERVAL_MS) return
  // A breather after first paint — the sync must never slow down the
  // 5-second lookup someone opened the app to make.
  setTimeout(() => {
    syncInformation().catch(() => {}) // silent by design; next launch retries
  }, 8000)
}

/**
 * The calendars' offline copy (Charles, 2026-10-14): with no service the
 * CDM Calendar and Session Builder show what was saved here, and say so,
 * instead of an empty-looking day. Saving means asking once while there is
 * a connection — the service worker keeps each answer (cache
 * 'cdm-calendar'). Every launch and every visit to either calendar asks
 * this; it does the work at most every 15 minutes.
 */
const CAL_KEY = 'cdm-calendars-saved-at'
const CAL_EVERY_MS = 15 * 60 * 1000

export function maybeSaveCalendars(user, delay = 4000) {
  if (!user || !offlineReady() || !navigator.onLine) return
  try {
    if (Date.now() - Number(localStorage.getItem(CAL_KEY) || 0) < CAL_EVERY_MS) return
    localStorage.setItem(CAL_KEY, String(Date.now())) // first, so two callers don't both run
  } catch {
    return
  }
  setTimeout(() => {
    saveCalendars(user).catch(() => {
      try { localStorage.removeItem(CAL_KEY) } catch { /* next launch tries again */ }
    })
  }, delay)
}

async function saveCalendars(user) {
  const today = todayISO()
  for (let i = 0; i < RANGE_DAYS; i++) await api(`/log/calendar?date=${addDays(today, i)}`)
  if (user.role !== 'admin') return
  await saveBuilderCopy()
  // Session Builder's screens load on first use; after a deploy that file
  // is new, and with no service it couldn't be fetched. Loading it here
  // puts it in the service worker's cache too.
  await import('../features/assistant/pages.js')
}
